import { defineStore } from "pinia"

import http from "~/services/http"
import { useSnackbarStore } from "~/stores/snackBarStore"
import type { StickyBoard, StickyNote } from "~/types/stickyBoard"

export const STICKY_COLORS = ["#C3FFCF", "#E0F3FF", "#FFF1AE", "#FFEDE8", "#EDE7FF"]

export function mixColor(hex: string, target: number, weight: number): string {
  const channels = [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16))
  return `rgb(${channels.map((channel) => Math.round(channel * weight + target * (1 - weight))).join(", ")})`
}

function emptyBoard(): StickyBoard {
  return { journal_id: null, sections_count: null, sections: [], show_names: true, question: null, notes: [] }
}

const saveTimers = new Map<string, ReturnType<typeof setTimeout>>()

function debounced(key: string, delay: number, callback: () => void) {
  clearTimeout(saveTimers.get(key))
  saveTimers.set(key, setTimeout(() => {
    saveTimers.delete(key)
    callback()
  }, delay))
}

export const useStickyBoardStore = defineStore("stickyBoardStore", {
  state: () => ({
    open: false,
    board: emptyBoard(),
  }),

  getters: {
    topZ: (state) => Math.max(0, ...state.board.notes.map((note) => note.z)),
  },

  actions: {
    setOpen(isOpen: boolean) {
      this.open = isOpen
      if (isOpen) {
        useGAEvent("teacher_tool_used", { tool: "sticky_board" })
        this.fetchBoard()
      }
    },

    fetchBoard() {
      http.get({
        url: "v2/sticky-notes/board",
        successCallback: (ctx) => {
          this.board = ctx.response._data.board
        },
      })
    },

    saveSettings(delay = 0) {
      debounced("settings", delay, () => {
        const { board } = this
        http.put({
          url: "v2/sticky-notes/board",
          body: {
            journal_id: board.journal_id,
            sections_count: board.sections_count,
            sections: board.sections,
            show_names: board.show_names,
            question: board.question,
          },
        })
      })
    },

    reset() {
      http.delete({
        url: "v2/sticky-notes/board",
        successCallback: (ctx) => {
          this.board = ctx.response._data.board
        },
      })
    },

    addNote() {
      http.post({
        url: "v2/sticky-notes/board/notes",
        successCallback: (ctx) => {
          this.board.notes.push(ctx.response._data.note)
        },
      })
    },

    receiveNote(note: StickyNote) {
      if (!this.board.notes.some((existing) => existing.id === note.id)) {
        this.board.notes.push(note)
      }
    },

    saveNote(note: StickyNote, delay = 0) {
      debounced(`note-${note.id}`, delay, () => {
        const { text, color, x, y, z, highlighted } = note
        http.put({ url: `v2/sticky-notes/board/notes/${note.id}`, body: { text, color, x, y, z, highlighted } })
      })
    },

    setNoteText(note: StickyNote, text: string) {
      note.text = text
      this.saveNote(note, 500)
    },

    setNoteColor(note: StickyNote, color: string) {
      note.color = color
      this.saveNote(note)
    },

    toggleNoteHighlight(note: StickyNote) {
      note.highlighted = !note.highlighted
      this.saveNote(note)
    },

    raiseNote(note: StickyNote) {
      if (note.z < this.topZ) {
        note.z = this.topZ + 1
      }
    },

    moveNote(note: StickyNote, x: number, y: number) {
      note.x = x
      note.y = y
    },

    deleteNote(note: StickyNote) {
      clearTimeout(saveTimers.get(`note-${note.id}`))
      this.board.notes = this.board.notes.filter((existing) => existing.id !== note.id)
      http.delete({ url: `v2/sticky-notes/board/notes/${note.id}` })
    },

    ask() {
      if (!this.board.journal_id) { return }
      http.post({
        url: "v2/sticky-notes/requests",
        body: { journal_id: this.board.journal_id, question: this.board.question },
        successCallback: () => {
          useSnackbarStore().showMessage({ text: "Līmlapas pieprasītas no skolēniem", type: "success" })
        },
      })
    },
  },
})
